import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { logger } from '@shared/lib/logger';
import { onSessionReset } from '@shared/session';
import { toast } from '@shared/ui/toast';

import { type ApiError, CLIENT_ERROR_CODE, isApiError } from './api-error';
import { getErrorMessage } from './error-message';

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiError;
    mutationMeta: {
      /** true 면 실패해도 기본 토스트를 띄우지 않는다(호출부가 직접 처리) */
      silent?: boolean;
    };
    queryMeta: {
      /** 조회 실패는 화면의 에러 상태로 보인다. 토스트가 꼭 필요할 때만 true */
      toastOnError?: boolean;
    };
  }
}

export const QUERY_DEFAULTS = {
  /** 도메인마다 `useQuery` 에서 다시 정한다. 0(항상 stale)을 기본으로 두지 않는다 */
  staleTimeMs: 30_000,
  gcTimeMs: 5 * 60_000,
  /** HTTP client 가 이미 GET 을 2회까지 재시도한다. Query 는 그 위에 1회만 */
  maxRetries: 1,
} as const;

/** 4xx · 취소는 다시 시도해도 결과가 같다. 네트워크 오류 · 타임아웃 · 5xx 만 재시도한다. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= QUERY_DEFAULTS.maxRetries) return false;
  if (!isApiError(error)) return false;
  if (isApiError(error, CLIENT_ERROR_CODE.CANCELED)) return false;
  if (isApiError(error, 'SERVICE_UNAVAILABLE')) return false;
  if (isApiError(error, [CLIENT_ERROR_CODE.NETWORK_ERROR, CLIENT_ERROR_CODE.TIMEOUT])) return true;
  return error.status >= 500;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        logger.warn('조회 실패', {
          ...(error.traceId ? { traceId: error.traceId } : {}),
          code: error.code,
          queryKey: query.queryKey,
        });
        if (query.meta?.toastOnError) toast.error(getErrorMessage(error));
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        logger.warn('변경 실패', {
          ...(error.traceId ? { traceId: error.traceId } : {}),
          code: error.code,
        });
        if (mutation.meta?.silent) return;
        // 폼 필드 오류는 필드에 표시한다(applyServerFieldErrors). 토스트는 요약만.
        toast.error(getErrorMessage(error));
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: QUERY_DEFAULTS.staleTimeMs,
        gcTime: QUERY_DEFAULTS.gcTimeMs,
        retry: shouldRetryQuery,
        // 창을 다시 볼 때마다 다시 부르지 않는다. 필요한 조회만 `refetchOnWindowFocus: true`
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        // 변경 요청은 재시도하지 않는다(멱등 키가 있을 때만 HTTP client 가 재시도한다).
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();

// 사용자 전환(로그아웃 · 세션 만료 · 대리 로그인) 때 캐시를 비운다.
onSessionReset(() => queryClient.clear());
